/// <amd-module name="Wit/WorkItemForm/Links/GitHub/GitHubLinkForm" />
import { useDebouncedSubscription, useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useService } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { isUrlValid } from "VSS/Platform/Util/Url";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { ExternalGitCommit, ExternalGitPullRequest, ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { useWorkItem } from "Wit/WorkItemForm/Common/Hooks";
import * as Resources from "Wit/WorkItemForm/Resources";
import { RegisteredLinkTypeNames } from "Wit/WorkItemLinksRegistration/Constants";
import { ILinkDisplayData, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { ArtifactsUpdatedCallback } from "Wit/WorkItemLinksRegistration/IRegisteredLinkTypeService";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/WorkItemModel/OM";
import * as React from "react";
import { useCallback, useMemo } from "react";
import { ArtifactLink } from "../Controls/ArtifactLink";
import { GitHubCommitSelection } from "./GitHubCommitSelection";
import { GitHubPullRequestSelection } from "./GitHubPullRequestSelection";
import { GitHubRepositorySelection } from "./GitHubRepositorySelection";

const moreInfoUrl = "https://aka.ms/azureboardsgithub/linkdialog";

interface IGitHubLinkFormProps {
    linkType: string;
    pageContext: IVssPageContext;
    onArtifactsUpdated: ArtifactsUpdatedCallback;
    getDisplayDataForGitHubUrl: (
        githubUrl: string,
        workItemId: number,
        pageContext: IVssPageContext
    ) => Promise<IResolvedLinkDisplayData | undefined>;
}

export function GitHubLinkForm({ linkType, onArtifactsUpdated, pageContext, getDisplayDataForGitHubUrl }: IGitHubLinkFormProps) {
    const telemetryService = useService<IVssTelemetryService>("IVssTelemetryService");
    const [loadingObservable, setLoading] = useObservable(false);
    const [forceShowSetUrlManuallyObservable, setForceShowSetUrlManually] = useObservable(false);
    const [gitHubUrlObservable, setGitHubUrl] = useObservable("");
    const [errorObservable, setError] = useObservable<string | undefined>(undefined);
    const [linkTargetObservable, setLinkTarget] = useObservable<ILinkDisplayData | undefined>(undefined);
    const [selectedRepositoryObservable, setSelectedRepository] = useObservable<ExternalGitRepo | undefined>(undefined);
    const [selectedPullRequestObservable, setSelectedPullRequest] = useObservable<ExternalGitPullRequest | undefined>(undefined);
    const [selectedCommitObservable, setSelectedCommit] = useObservable<ExternalGitCommit | undefined>(undefined);
    const selectableGitHubPullRequestsFFEnabled = useMemo(
        () => isFeatureFlagEnabled(pageContext, "WebAccess.WorkItemTracking.EnableSelectableGitHubPullRequests", false),
        []
    );
    const selectableGitHubCommitsFFEnabled = useMemo(
        () => isFeatureFlagEnabled(pageContext, "WebAccess.WorkItemTracking.EnableSelectableGitHubCommits", false),
        []
    );
    const cacheGitHubArtifactsFFEnabled = useMemo(
        () => isFeatureFlagEnabled(pageContext, "WebAccess.WorkItemTracking.CacheGitHubArtifacts", false),
        []
    );
    const workItem = useWorkItem();

    const onError = useCallback(
        (errorMessage: string) => {
            setError(errorMessage);
            setLinkTarget(undefined);
            onArtifactsUpdated([]);
        },
        [onArtifactsUpdated]
    );

    const displayStrings = getDisplayStrings(linkType);
    if (!displayStrings) {
        return <div>Unsupported link type {linkType}</div>;
    }
    const { itemTypeDescription, label, placeholder } = displayStrings;

    useDebouncedSubscription(
        gitHubUrlObservable,
        200,
        async newGitHubUrl => {
            setLinkTarget(undefined);
            onArtifactsUpdated([]);

            if (!isUrlValid(newGitHubUrl)) {
                onError(format(Resources.MissingGitHubItemUrl, itemTypeDescription));
                return;
            }

            setLoading(true);
            let gitHubLinkDisplayData;
            try {
                gitHubLinkDisplayData = await getDisplayDataForGitHubUrl(newGitHubUrl, workItem.id, pageContext);
            } catch (e) {
                onError(e.message);
                setLoading(false);
                return;
            }

            if (newGitHubUrl !== gitHubUrlObservable.value) {
                // The url has been changed since we sent the request; do nothing with the response.
                return;
            }

            setLoading(false);

            if (!gitHubLinkDisplayData) {
                onError(format(Resources.InvalidGitHubItemUrl, itemTypeDescription));
                return;
            }

            // Fetching link data was successful
            setLinkTarget(gitHubLinkDisplayData);
            onArtifactsUpdated([gitHubLinkDisplayData]);

            // Clear the error if it exists
            if (errorObservable.value) {
                setError(undefined);
            }

            telemetryService.publishEvent(WITCustomerIntelligenceArea.WORK_ITEM_TRACKING, WITCustomerIntelligenceFeature.GITHUB_ADD_LINK, {
                linkType: linkType,
                improvedPRsExperience: selectableGitHubPullRequestsFFEnabled,
                improvedCommitsExperience: selectableGitHubCommitsFFEnabled,
                setUrlManually: forceShowSetUrlManuallyObservable.value
            });
        },
        [itemTypeDescription]
    );

    return (
        <>
            <div className="github-link-form-more-info margin-bottom-8 padding-vertical-8 padding-horizontal-16">
                <Icon className="icon-margin" iconName="Info" />
                <a href={moreInfoUrl} target="_blank">
                    {Resources.GitHubIntegrationMessage}
                </a>
            </div>
            {((selectableGitHubPullRequestsFFEnabled && linkType === RegisteredLinkTypeNames.GitHubPullRequestLinkType) ||
                (selectableGitHubCommitsFFEnabled && linkType === RegisteredLinkTypeNames.GitHubCommitLinkType)) && (
                <Observer
                    selectedRepository={selectedRepositoryObservable}
                    selectedPullRequest={selectedPullRequestObservable}
                    selectedCommit={selectedCommitObservable}
                    forceShowSetUrlManually={forceShowSetUrlManuallyObservable}
                >
                    {({ selectedRepository, selectedPullRequest, selectedCommit, forceShowSetUrlManually }) => {
                        const repositorySelection = (
                            <div className="margin-bottom-8">
                                <GitHubRepositorySelection
                                    pageContext={pageContext}
                                    selectedRepository={selectedRepository}
                                    setSelectedRepository={repository => {
                                        if (repository?.id !== selectedRepository?.id) {
                                            setSelectedRepository(repository);
                                            setSelectedPullRequest(undefined);
                                        }
                                    }}
                                    setShowSetUrlManually={setForceShowSetUrlManually}
                                    cacheGitHubArtifactsFFEnabled={cacheGitHubArtifactsFFEnabled}
                                />
                            </div>
                        );
                        return !forceShowSetUrlManually ? (
                            selectableGitHubPullRequestsFFEnabled && linkType === RegisteredLinkTypeNames.GitHubPullRequestLinkType ? (
                                <>
                                    <div className="margin-bottom-8">
                                        <FormatComponent format={Resources.GithubFallbackToPastingUrl}>
                                            <Link
                                                ariaLabel={Resources.GithubFallbackToPastingUrlLink}
                                                onClick={() => {
                                                    setForceShowSetUrlManually(true);
                                                }}
                                            >
                                                {Resources.GithubFallbackToPastingUrlLink}
                                            </Link>
                                        </FormatComponent>
                                    </div>
                                    {repositorySelection}
                                    <GitHubPullRequestSelection
                                        pageContext={pageContext}
                                        selectedRepository={selectedRepository}
                                        selectedPullRequest={selectedPullRequest}
                                        setSelectedPullRequest={pullRequest => {
                                            setSelectedPullRequest(pullRequest);
                                            setGitHubUrl(pullRequest?.webUrl || "");
                                        }}
                                        setShowSetUrlManually={setForceShowSetUrlManually}
                                        cacheGitHubArtifactsFFEnabled={cacheGitHubArtifactsFFEnabled}
                                    />
                                </>
                            ) : selectableGitHubCommitsFFEnabled && linkType === RegisteredLinkTypeNames.GitHubCommitLinkType ? (
                                <>
                                    <div className="margin-bottom-8">
                                        <FormatComponent format={Resources.GithubCommitsSearchOnlyInDefaultBranchInfo}>
                                            <Link
                                                ariaLabel={Resources.GithubFallbackToPastingUrlLink}
                                                onClick={() => {
                                                    setForceShowSetUrlManually(true);
                                                }}
                                            >
                                                {Resources.GithubFallbackToPastingUrlLink}
                                            </Link>
                                        </FormatComponent>
                                    </div>
                                    <div className="margin-bottom-8">{repositorySelection}</div>
                                    <GitHubCommitSelection
                                        pageContext={pageContext}
                                        selectedRepository={selectedRepository}
                                        selectedCommit={selectedCommit}
                                        setSelectedCommit={commit => {
                                            setSelectedCommit(commit);
                                            setGitHubUrl(commit?.webUrl || "");
                                        }}
                                        setShowSetUrlManually={setForceShowSetUrlManually}
                                        cacheGitHubArtifactsFFEnabled={cacheGitHubArtifactsFFEnabled}
                                    />
                                </>
                            ) : null
                        ) : null;
                    }}
                </Observer>
            )}
            <Observer error={errorObservable} forceShowSetUrlManually={forceShowSetUrlManuallyObservable}>
                {({ error, forceShowSetUrlManually }: { error: string | undefined; forceShowSetUrlManually: boolean }) => (
                    <>
                        {(!(
                            (linkType === RegisteredLinkTypeNames.GitHubPullRequestLinkType && selectableGitHubPullRequestsFFEnabled) ||
                            (linkType === RegisteredLinkTypeNames.GitHubCommitLinkType && selectableGitHubCommitsFFEnabled)
                        ) ||
                            error ||
                            forceShowSetUrlManually) && (
                            <FormItem label={label} error={!!error} message={error}>
                                <TextField
                                    value={gitHubUrlObservable}
                                    placeholder={placeholder}
                                    onChange={(_e, val) => {
                                        setGitHubUrl(val);
                                    }}
                                />
                            </FormItem>
                        )}
                    </>
                )}
            </Observer>
            <Observer newLink={linkTargetObservable} loading={loadingObservable}>
                {({ newLink, loading }: { newLink: ILinkDisplayData | undefined; loading: boolean }) => {
                    if (loading) {
                        return (
                            <div className="padding-16 flex-row justify-center">
                                <Spinner />
                            </div>
                        );
                    }

                    if (!newLink) {
                        return null;
                    }

                    return <div className="padding-16">{newLink && <ArtifactLink showDetails artifactData={newLink} />}</div>;
                }}
            </Observer>
        </>
    );
}

interface GitHubLinkTypeDisplayStrings {
    itemTypeDescription: string;
    label: string;
    placeholder: string;
}

function getDisplayStrings(linkType: string): GitHubLinkTypeDisplayStrings | undefined {
    switch (linkType) {
        case RegisteredLinkTypeNames.GitHubPullRequestLinkType:
            return {
                itemTypeDescription: Resources.GitHubPullRequestDescription,
                label: Resources.GitHubPullRequestUrlLabel,
                placeholder: Resources.GitHubPullRequestUrlPlaceholder
            };
        case RegisteredLinkTypeNames.GitHubCommitLinkType:
            return {
                itemTypeDescription: Resources.GitHubCommitDescription,
                label: Resources.GitHubCommitUrlLabel,
                placeholder: Resources.GitHubCommitUrlPlaceholder
            };
        case RegisteredLinkTypeNames.GitHubIssueLinkType:
            return {
                itemTypeDescription: Resources.GitHubIssueDescription,
                label: Resources.GitHubIssueUrlLabel,
                placeholder: Resources.GitHubIssueUrlPlaceholder
            };
        default:
            return undefined;
    }
}
